import dayjs from "dayjs";
import { api } from "@service/api";
import { useEffect, useState } from "react";

import { Card } from "@components/cards/card";
import { Input } from "@components/forms/input";
import { Pagination } from "@components/pagination";
import { Masks } from "@hooks/functions/Masks/Masks";
import { LoadingSkeleton } from "@components/skeleton-loading";
import { FaBroom, FaExpand, FaMinus } from "react-icons/fa6";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import {
  CustomerNotificationDTO,
  CustomerNotificationStatusEnum,
  CustomerNotificationStatusTypes,
} from "@hooks/dtos/ICustomerNotificationDTO";
import {
  getNotificationProperty,
  getNotificationReadActionStatus,
  getNotificationStatusProperty,
  changeNotificationStatus,
  getNotificationHideActionStatus,
} from "@hooks/functions/Notification/Notification";
import Swal from "sweetalert2";
import { Select } from "@components/forms/select";
import { customerNotificationStatusSelect } from "@hooks/functions/Notification/status";
import {
  NotificationCategoryDTO,
  NotificationCategoryStatusTypes,
} from "@hooks/dtos/INotificationCategoryDTO";
import {
  NotificationSubCategoryDTO,
  NotificationSubCategoryGroupDTO,
  NotificationSubCategoryGroupKeyDTO,
  NotificationSubCategoryGroupKeyTypes,
  NotificationSubCategoryStatusTypes,
} from "@hooks/dtos/INotificationSubCategoryDTO";
import { FaEnvelopeOpenText, FaRegBell } from "react-icons/fa";
import { ModalNotificationView } from "@components/modals/modal-notification-view";

interface INotificationListParams {
  page: number;
  startAt: string;
  endAt: string;
}

const masks = new Masks();
export function NotificationList() {
  // --- Default Variables --- //
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  // --- Another Control --- //
  const [show, setShow] = useState<{ filter: boolean }>({
    filter: false,
  });
  const [isLoading, setIsLoading] = useState({
    history: true,
    categories: true,
    subcategories: true,
    groupes: true,
  });

  // Pagination Part
  const [page, setPage] = useState<number>(1);
  const [take, setTake] = useState<number>(10);
  const [order, setOrder] = useState("DESC");
  const [totalPages, setTotalPages] = useState<number>(1);

  // --- Notification Modal --- //
  const [notificationId, setNotificationId] = useState<number>();

  // --- Bulk Selection --- //
  const [notificationIds, setNotificationIds] = useState<number[]>([]);
  const [bulkStatus, setBulkStatus] = useState<
    CustomerNotificationStatusTypes | ""
  >("");

  // Filters
  const [resultTime, setResultTime] = useState("Todo Período");
  const [showHidden, setShowHidden] = useState<boolean>(false);
  const [status, setStatus] = useState<CustomerNotificationStatusTypes | "">();
  const [category, setCategory] = useState<
    NotificationCategoryStatusTypes | ""
  >();
  const [subCategory, setSubCategory] = useState<
    NotificationSubCategoryStatusTypes | ""
  >();
  const [groupKey, setGroupKey] = useState<
    NotificationSubCategoryGroupKeyTypes | ""
  >();
  const [startAt, setStartDate] = useState("");
  const [endAt, setEndDate] = useState("");

  // --- Loading Info --- //
  const [notifications, setNotifications] = useState<CustomerNotificationDTO[]>(
    [],
  );
  const [notificationCategories, setNotificationCategories] = useState<
    NotificationCategoryDTO[]
  >([]);
  const [notificationSubCategories, setNotificationSubCategories] = useState<
    NotificationSubCategoryDTO[]
  >([]);
  const [notificationSubCategoryGroups, setNotificationSubCategoryGroups] =
    useState<NotificationSubCategoryGroupDTO[]>([]);

  // Notification Table List
  const getNotificationList = async ({
    page,
    startAt,
    endAt,
  }: INotificationListParams) => {
    setIsLoading((prev) => ({ ...prev, history: true }));
    try {
      const params = `?${new URLSearchParams(
        Object.entries({
          page: (page || 1).toString(),
          take: take.toString(),
          showHidden: showHidden.toString(),
          ...(category && { category }),
          ...(subCategory && { subCategory }),
          ...(groupKey && { groupKey }),
          ...(order && { orderBy: order }),
          ...(status && { status }),
          ...(startAt && { startAt }),
          ...(endAt && { endAt }),
        }),
      ).toString()}`;
      const result = await api.get(`/notification/list${params}`);
      // console.log({ params, data: result.data });

      setNotifications(result.data.list);
      setTotalPages(result.data.totalPages);
      setIsLoading((prev) => ({ ...prev, history: false }));
    } catch (err: any) {
      setIsLoading((prev) => ({ ...prev, history: false }));
      pushToastify({
        message: err?.response?.data.message ?? "Ocorreu um erro!",
        type: "error",
        theme: "dark",
      });
    }
  };

  // Load the Category List
  const getNotificationCategoryList = async () => {
    setIsLoading((prev) => ({ ...prev, categories: true }));
    try {
      const result = await api.get(`/notification/categories`);
      setNotificationCategories(result.data);
      setIsLoading((prev) => ({ ...prev, categories: false }));
    } catch (err: any) {
      setIsLoading((prev) => ({ ...prev, categories: false }));
      pushToastify({
        message: err?.response?.data.message ?? "Ocorreu um erro!",
        type: "error",
        theme: "dark",
      });
    }
  };

  // Load the SubCategory List
  const getNotificationSubCategoryList = async () => {
    setIsLoading((prev) => ({ ...prev, categories: true }));
    try {
      const result = await api.get(`/notification/subCategories`);
      // console.log({ f: "getNotificationSubCategoryList", result });
      setNotificationSubCategories(result.data.list);
      setNotificationSubCategoryGroups(result.data.groupies);
      setIsLoading((prev) => ({ ...prev, categories: false }));
    } catch (err: any) {
      setIsLoading((prev) => ({ ...prev, categories: false }));
      pushToastify({
        message: err?.response?.data.message ?? "Ocorreu um erro!",
        type: "error",
        theme: "dark",
      });
    }
  };

  const handleChangeNotificationStatus = async (
    notificationId: number,
    status: CustomerNotificationStatusTypes,
    message: string,
  ) => {
    Swal.fire({
      title: "Alterar Status?",
      text: message,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sim, continuar!",
      cancelButtonText: "Cancelar",
    }).then(async (result: { isConfirmed: boolean }) => {
      if (result.isConfirmed) {
        try {
          await changeNotificationStatus({
            notificationId,
            status,
          });

          getNotificationList({
            page,
            startAt,
            endAt,
          });
        } catch (err: any) {
          pushToastify({
            message: err.message || "Ocorreu um erro!",
            type: "error",
            theme: "dark",
          });
        }
      }
    });
  };

  const handleChangeNotificationStatusBulk = async () => {
    if (!notificationIds.length || !bulkStatus) return;
    try {
      await api.put(`/notification/bulk/status`, {
        notificationIds,
        status: bulkStatus,
      });
      setNotificationIds([]);
      setBulkStatus("");
      getNotificationList({ page, startAt, endAt });
    } catch (err: any) {
      pushToastify({
        message: err?.response?.data.message ?? "Ocorreu um erro!",
        type: "error",
        theme: "dark",
      });
    }
  };

  async function cleanFilters() {
    // --- Main Filters --- //
    setStatus(undefined);
    setShowHidden(false);
    setCategory(undefined);
    setSubCategory(undefined);
    setGroupKey(undefined);

    // --- Anothers --- //
    setStartDate("");
    setEndDate("");
    handleSubmit("", "");
  }

  function handleSubmit(_startDate: string, _endDate: string) {
    setIsLoading((prev) => ({ ...prev, history: true }));
    setResultTime(
      _startDate.length + _endDate.length > 0
        ? `Período Selecionado`
        : `Todo Período`,
    );
    getNotificationList({
      page,
      startAt: _startDate,
      endAt: _endDate,
    });
  }

  const isAllSelected =
    notifications.length > 0 &&
    notifications.every((n) => notificationIds.includes(n.id));

  const isIndeterminate = notificationIds.length > 0 && !isAllSelected;

  function toggleSelectAll() {
    if (isAllSelected) {
      setNotificationIds([]);
    } else {
      setNotificationIds(notifications.map((n) => n.id));
    }
  }

  function toggleSelectOne(id: number) {
    setNotificationIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  useEffect(() => {
    getNotificationList({
      page,
      startAt,
      endAt,
    });
  }, [page]);

  // When category changes, validate both groupKey and subCategory against the new category.
  // Resets each one independently if they no longer belong to the selected category.
  useEffect(() => {
    if (!category) return;

    const validGroups = notificationSubCategoryGroups
      .filter((x) => x.categoryId === Number(category))
      .flatMap((x) => x.groups.map((g) => g.groupKey));

    const groupIsInvalid = groupKey && !validGroups.includes(groupKey as any);

    if (groupIsInvalid) setGroupKey(undefined);

    const validSubCategories = notificationSubCategories
      .filter((x) => x.category === Number(category))
      .map((x) => String(x.id));

    const subCategoryIsInvalid =
      subCategory && !validSubCategories.includes(String(subCategory));

    if (subCategoryIsInvalid) setSubCategory(undefined);
  }, [category]);

  // When groupKey changes, validate only subCategory against the new group.
  useEffect(() => {
    if (!groupKey || !subCategory) return;

    const validSubCategories = notificationSubCategories
      .filter((x) => x.groupKey === groupKey)
      .map((x) => String(x.id));

    if (!validSubCategories.includes(String(subCategory))) {
      setSubCategory(undefined);
    }
  }, [groupKey]);

  useEffect(() => {
    getNotificationCategoryList();
    getNotificationSubCategoryList();
    getNotificationList({
      page,
      startAt,
      endAt,
    });
  }, []);

  return (
    <>
      {/* Filters */}
      <div className="row">
        <div className="col-md-12">
          <Card>
            {/* Header */}
            <div
              className="card-header"
              style={{ cursor: "pointer" }}
              onClick={() => {
                setShow((prev) => ({
                  ...prev,
                  filter: !show.filter,
                }));
              }}
            >
              <div className="d-flex justify-content-between">
                <div className="">
                  <div className="card-title">Filtrar Registros</div>
                  <div className="card-category">
                    Utilize o filtro para trazer de contexto específicos.
                  </div>
                </div>
                <div className="">
                  {show.filter ? (
                    <FaMinus
                      className="fs-5"
                      title="Clique para ocultar as informações."
                    />
                  ) : (
                    <FaExpand
                      className="fs-5"
                      title="Clique para visualizar as informações."
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Body */}
            <div className={`card-body ${show.filter ? `` : `d-none`}`}>
              {/* Another Filters */}
              <div className="row mt-2">
                {/* Category */}
                <div className="col-md-4">
                  <Select
                    animated
                    label="Categoria"
                    className="form-select form-control"
                    aria-label="category"
                    value={category || ""}
                    options={[
                      ...notificationCategories.map((x) => ({
                        value: x.id,
                        name: x.title,
                      })),
                    ]}
                    onChange={(e) => {
                      setCategory(
                        e.target.value as NotificationCategoryStatusTypes | "",
                      );
                    }}
                  />
                </div>

                {/* Groupies */}
                <div className="col-md-4">
                  <Select
                    animated
                    label="Grupos"
                    className="form-select form-control"
                    aria-label="groupKey"
                    value={groupKey || ""}
                    options={[
                      ...notificationSubCategoryGroups
                        .filter(
                          (x) => !category || x.categoryId === Number(category),
                        )
                        .flatMap((x) =>
                          x.groups.map((group) => ({
                            value: group.groupKey,
                            name: group.groupLabel,
                          })),
                        ),
                    ]}
                    onChange={(e) => {
                      setGroupKey(
                        e.target.value as
                          | NotificationSubCategoryGroupKeyTypes
                          | "",
                      );
                    }}
                  />
                </div>

                {/* SubCategory */}
                <div className="col-md-4">
                  <Select
                    animated
                    label="Tópico (Sub-categoria)"
                    className="form-select form-control"
                    aria-label="subCategory"
                    value={subCategory || ""}
                    options={[
                      ...notificationSubCategories
                        .filter(
                          (x) => !category || x.category === Number(category),
                        )
                        .map((y) => ({
                          value: y.id,
                          name: y.title,
                        })),
                    ]}
                    onChange={(e) => {
                      setSubCategory(
                        e.target.value as
                          | NotificationSubCategoryStatusTypes
                          | "",
                      );
                    }}
                  />
                </div>
              </div>

              <div className="row mt-3">
                {/* Hidden */}
                <div className="col-md-2">
                  <Select
                    animated
                    label="Mostrar Ocultas"
                    className="form-select form-control"
                    aria-label="hidden"
                    value={showHidden ? "true" : "false"}
                    options={[
                      { value: "true", name: "Sim" },
                      { value: "false", name: "Não" },
                    ]}
                    onChange={(e) => {
                      setShowHidden(e.target.value == "true" ? true : false);
                    }}
                  />
                </div>

                {/* Status */}
                <div className="col-md-2">
                  <Select
                    animated
                    label="Status da Mensagem"
                    className="form-select form-control"
                    aria-label="status"
                    value={status || ""}
                    options={[
                      { value: "", name: "Todos" },
                      ...customerNotificationStatusSelect.map((x) => ({
                        value: x.status,
                        name: x.text,
                      })),
                    ]}
                    onChange={(e) => {
                      setStatus(
                        e.target.value as CustomerNotificationStatusTypes | "",
                      );
                    }}
                  />
                </div>

                {/* Date: StartAt */}
                <div className="col-md-2">
                  <Input
                    animated
                    id="startAt"
                    label="Data Inicial"
                    placeholder=""
                    type="date"
                    value={startAt}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>

                {/* Date: EndAt */}
                <div className="col-md-2">
                  <Input
                    animated
                    id="endAt"
                    label="Data Final"
                    placeholder=""
                    type="date"
                    value={endAt}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>

                {/* Buttons */}
                <div className="col-md-4">
                  <div className="d-flex justify-content-between">
                    <div className="form-group w-100">
                      <button
                        className="btn btn-sm btn-success form-control rounded px-5"
                        onClick={() => handleSubmit(startAt, endAt)}
                      >
                        Filtrar
                      </button>
                      <small className="text-muted ms-1"> </small>
                    </div>
                    <div className="form-group w-100">
                      <button
                        className="btn btn-sm btn-warning form-control rounded text-white"
                        onClick={cleanFilters}
                        title="Limpar todos os filtros."
                      >
                        <FaBroom /> Limpar
                      </button>
                      <small className="text-muted ms-1"> </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Table */}
      <div className="row">
        <div className="col-md-12">
          <Card>
            <div className="card-header">
              <div className="row">
                {/* Header of the Table */}
                <div className="col-md-8">
                  <h4 className="card-title">Suas Notificações</h4>
                  <p className="card-category">
                    Suas notificações na plataforma
                  </p>
                </div>
                {/* Ordenate Table */}
                <div className="col-md-4">
                  <div className="row align-items-center">
                    <div className="col-md-4">
                      <div className="form-group">
                        <select
                          className="form-select"
                          aria-label="Quantidade de Registros por Página"
                          value={take}
                          onChange={(e) => setTake(Number(e.target.value))}
                        >
                          <option value="10">10</option>
                          <option value="25">25</option>
                          <option value="50">50</option>
                          <option value="75">75</option>
                          <option value="100">100</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <select
                          className="form-select"
                          aria-label="Ordenação da Tabela"
                          value={order}
                          onChange={(e) => setOrder(e.target.value)}
                        >
                          <option value="ASC">Crescente</option>
                          <option value="DESC">Decrescente</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <button
                          className="btn btn-sm btn-success form-control px-2"
                          onClick={() => handleSubmit(startAt, endAt)}
                        >
                          Aplicar
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="card-body">
              {/* Bulk Action Bar — visible only when items are selected */}
              {notificationIds.length > 0 && (
                <div className="d-flex align-items-center gap-3 mb-3 p-2 rounded border bg-light">
                  <span className="text-muted small fw-semibold">
                    {notificationIds.length} selecionada(s)
                  </span>
                  <Select
                    animated
                    label="Status da Notificação"
                    className="form-select form-control"
                    aria-label="bulkStatus"
                    value={bulkStatus}
                    options={[
                      {
                        value: CustomerNotificationStatusEnum.FAKE_READ,
                        name: "Lida",
                      },
                      {
                        value: CustomerNotificationStatusEnum.UNREAD,
                        name: "Não lida",
                      },
                      {
                        value: CustomerNotificationStatusEnum.HIDDEN,
                        name: "Oculta",
                      },
                    ]}
                    onChange={(e) =>
                      setBulkStatus(
                        e.target.value as CustomerNotificationStatusTypes | "",
                      )
                    }
                  />
                  <button
                    className="btn btn-sm btn-primary"
                    disabled={!bulkStatus}
                    onClick={handleChangeNotificationStatusBulk}
                  >
                    Aplicar em massa
                  </button>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setNotificationIds([])}
                  >
                    Limpar seleção
                  </button>
                </div>
              )}

              <div className="table-responsive">
                <div
                  id="basic-datatables_wrapper"
                  className="dataTables_wrapper container-fluid dt-bootstrap4"
                >
                  <div
                    id="basic-datatables_length"
                    className="dataTables_length"
                  >
                    <table className="table table-striped">
                      <thead>
                        <tr role="row">
                          <th>
                            <input
                              type="checkbox"
                              checked={isAllSelected}
                              ref={(el) => {
                                if (el) el.indeterminate = isIndeterminate;
                              }}
                              onChange={toggleSelectAll}
                              title="Select all"
                            />
                          </th>
                          <th>#</th>
                          <th>Categoria</th>
                          <th>Grupo</th>
                          <th>Tópico</th>
                          <th>Assunto</th>
                          <th>Status</th>
                          <th>Data</th>
                          <th>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {isLoading.history && (
                          <tr>
                            <td colSpan={8} className="text-center">
                              <LoadingSkeleton />
                            </td>
                          </tr>
                        )}

                        {notifications &&
                          notifications.length > 0 &&
                          notifications.map((notification) => {
                            const notificationProperty =
                              getNotificationProperty({
                                subCategoryId: notification.subCategoryData.id,
                                groupKey: notification.subCategoryData.groupKey,
                              });

                            const action = getNotificationReadActionStatus({
                              status: notification.status,
                            });

                            const hide = getNotificationHideActionStatus({
                              status: notification.status,
                            });

                            const status = getNotificationStatusProperty({
                              status: notification.status,
                            });

                            return (
                              <tr key={notification?.id}>
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={notificationIds.includes(
                                      notification.id,
                                    )}
                                    onChange={() =>
                                      toggleSelectOne(notification.id)
                                    }
                                    title={`Select notification #${notification.id}`}
                                  />
                                </td>
                                <td className="">{notification.id}</td>
                                <td
                                  className=""
                                  title={`${notification?.categoryData?.title}: ${notification?.categoryData?.description}`}
                                >
                                  <span
                                    className="rounded p-2"
                                    style={{
                                      backgroundColor:
                                        notificationProperty.bgColor,
                                    }}
                                  >
                                    <notificationProperty.icon
                                      className="fs-5"
                                      style={{
                                        color: notificationProperty.textColor,
                                      }}
                                    />
                                  </span>
                                </td>
                                <td className="">
                                  {notification?.subCategoryData?.groupLabel}
                                </td>
                                <td
                                  className=""
                                  title={
                                    notification?.subCategoryData?.description
                                  }
                                >
                                  {notification?.subCategoryData?.title}
                                </td>
                                <td className="" title={notification.title}>
                                  {notification.title}
                                </td>
                                <td>
                                  <span
                                    className={`badge ${status.textColor}`}
                                    style={{ backgroundColor: status.bgColor }}
                                    title={status.description}
                                  >
                                    {status.text}
                                  </span>
                                </td>
                                <td>
                                  {dayjs(notification?.createdAt).format(
                                    "DD/MM/YYYY [ás] HH:MM:ss",
                                  )}
                                </td>
                                <td>
                                  <div className="d-flex gap-2">
                                    {/* See the Notification */}
                                    <span
                                      style={{ cursor: "pointer" }}
                                      title={"Ver notificação"}
                                      onClick={async () => {
                                        setNotificationId(notification.id);
                                      }}
                                    >
                                      <FaEnvelopeOpenText className="fs-4" />
                                    </span>

                                    {/* Read | Unread Notification */}
                                    {action ? (
                                      <span
                                        style={{ cursor: "pointer" }}
                                        title={action.text}
                                        onClick={() => {
                                          handleChangeNotificationStatus(
                                            notification.id,
                                            action.newStatus,
                                            action.text,
                                          );
                                        }}
                                      >
                                        <action.icon className="fs-4" />
                                      </span>
                                    ) : (
                                      <span></span>
                                    )}

                                    {/* Hide | Show Notification */}
                                    <span
                                      style={{ cursor: "pointer" }}
                                      title={hide.text}
                                      onClick={() => {
                                        handleChangeNotificationStatus(
                                          notification.id,
                                          hide.newStatus,
                                          hide.text,
                                        );
                                      }}
                                    >
                                      <hide.icon className="fs-4" />
                                    </span>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}

                        {!isLoading.history && notifications?.length <= 0 && (
                          <tr>
                            <td colSpan={10} className="text-center">
                              Nenhum registro encontrado.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <Pagination
                page={page}
                setPage={setPage}
                totalPages={totalPages}
                maxPageShow={3}
              />
            </div>
          </Card>
        </div>
      </div>

      {notificationId && (
        <ModalNotificationView
          notificationId={notificationId}
          isOpen={notificationId > 0}
          onClose={(updated) => {
            setNotificationId(undefined);
            if (updated) {
              getNotificationList({ page, startAt, endAt });
            }
          }}
        />
      )}
    </>
  );
}
